import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ASSIGNEES,
  CATEGORIES,
  CATEGORY_META,
  type Assignee,
  type Category,
  type ListItem,
} from '@travel-planner/shared';
import { AssigneeChip, CategoryBadge, assigneeLabel } from './itemUi';
import { useDeleteItem, useUpdateItem } from './useListItems';
import { useMapContext } from '../map/MapContext';
import { CommentThread, VoteRow } from './ItemSocial';

export function ItemCard({
  item,
  tripId,
  countryId,
  dragDisabled,
}: {
  item: ListItem;
  tripId: string;
  countryId: string;
  dragDisabled: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    disabled: dragDisabled,
  });
  const update = useUpdateItem(tripId, countryId);
  const del = useDeleteItem(tripId, countryId);
  const { focus, startPlacing } = useMapContext();
  const placed = item.lat !== null && item.lng !== null;
  const [editing, setEditing] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    background: 'var(--surface)',
    padding: 10,
    marginBottom: 8,
    boxShadow: 'var(--shadow-sm)',
  };

  if (editing) {
    return (
      <div ref={setNodeRef} style={style}>
        <ItemEditor
          item={item}
          onCancel={() => setEditing(false)}
          onSave={(patch) => update.mutate({ itemId: item.id, patch }, { onSuccess: () => setEditing(false) })}
          saving={update.isPending}
        />
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style}>
      <div className="d-flex align-items-start gap-2">
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          aria-label="Drag"
          disabled={dragDisabled}
          className="btn btn-ghost btn-sm p-0 text-body-tertiary"
          style={{ cursor: dragDisabled ? 'default' : 'grab', minHeight: 'auto', touchAction: 'none' }}
        >
          ⠿
        </button>
        <input
          type="checkbox"
          className="form-check-input mt-1 flex-shrink-0"
          checked={item.done}
          onChange={(e) => update.mutate({ itemId: item.id, patch: { done: e.target.checked } })}
        />
        <div className="flex-grow-1" style={{ minWidth: 0 }}>
          <div
            style={{
              textDecoration: item.done ? 'line-through' : 'none',
              color: item.done ? 'var(--text-dim)' : 'var(--text)',
              wordBreak: 'break-word',
            }}
          >
            {item.title}
          </div>
          {item.note && (
            <div className="small text-secondary mt-1" style={{ whiteSpace: 'pre-wrap' }}>
              {item.note}
            </div>
          )}
          <div className="d-flex gap-2 mt-2 align-items-center flex-wrap">
            <CategoryBadge category={item.category} />
            <AssigneeChip assignee={item.assignee} />
            {item.link && (
              <a href={item.link} target="_blank" rel="noreferrer" style={{ fontSize: '0.75rem' }}>
                link ↗
              </a>
            )}
            {placed ? (
              <button
                className="btn btn-ghost btn-sm p-0 text-info"
                onClick={() => focus(item.id)}
                title="Show on map"
                style={{ fontSize: '0.75rem', minHeight: 'auto', color: 'var(--accent)' }}
              >
                📍 map
              </button>
            ) : (
              <button
                className="btn btn-ghost btn-sm p-0 text-body-tertiary"
                onClick={() => startPlacing(item.id)}
                title="Place on the map"
                style={{ fontSize: '0.75rem', minHeight: 'auto' }}
              >
                📍 place
              </button>
            )}
          </div>
          <div className="mt-2">
            <VoteRow item={item} tripId={tripId} countryId={countryId} />
          </div>
        </div>
        <div className="d-flex flex-column gap-1">
          <button className="btn btn-ghost btn-sm p-1" onClick={() => setEditing(true)} title="Edit" style={{ minHeight: 'auto' }}>
            ✎
          </button>
          <button
            className="btn btn-ghost btn-sm p-1"
            onClick={() => setShowComments((v) => !v)}
            title="Comments"
            style={{ minHeight: 'auto' }}
          >
            💬
          </button>
          <button
            className="btn btn-ghost btn-sm p-1 text-danger"
            onClick={() => {
              if (confirm(`Delete "${item.title}"?`)) del.mutate(item.id);
            }}
            title="Delete"
            style={{ minHeight: 'auto' }}
          >
            ✕
          </button>
        </div>
      </div>
      {showComments && <CommentThread tripId={tripId} itemId={item.id} />}
    </div>
  );
}

function ItemEditor({
  item,
  onSave,
  onCancel,
  saving,
}: {
  item: ListItem;
  onSave: (patch: {
    title: string;
    category: Category;
    assignee: Assignee;
    note: string | null;
    link: string | null;
  }) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [title, setTitle] = useState(item.title);
  const [category, setCategory] = useState<Category>(item.category);
  const [assignee, setAssignee] = useState<Assignee>(item.assignee);
  const [note, setNote] = useState(item.note ?? '');
  const [link, setLink] = useState(item.link ?? '');

  return (
    <form
      className="d-flex flex-column gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!title.trim()) return;
        onSave({
          title: title.trim(),
          category,
          assignee,
          note: note.trim() ? note.trim() : null,
          link: link.trim() ? link.trim() : null,
        });
      }}
    >
      <input className="form-control form-control-sm" value={title} onChange={(e) => setTitle(e.target.value)} />
      <div className="d-flex gap-2">
        <select className="form-select form-select-sm" style={{ flex: 1 }} value={category} onChange={(e) => setCategory(e.target.value as Category)}>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_META[c].icon} {CATEGORY_META[c].label}
            </option>
          ))}
        </select>
        <select className="form-select form-select-sm" style={{ width: 'auto' }} value={assignee} onChange={(e) => setAssignee(e.target.value as Assignee)}>
          {ASSIGNEES.map((a) => (
            <option key={a} value={a}>
              {assigneeLabel(a)}
            </option>
          ))}
        </select>
      </div>
      <textarea
        className="form-control form-control-sm"
        placeholder="Note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        style={{ resize: 'vertical' }}
      />
      <input
        className="form-control form-control-sm"
        placeholder="https://link (optional)"
        value={link}
        onChange={(e) => setLink(e.target.value)}
      />
      <div className="d-flex gap-2">
        <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !title.trim()}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
